import { FC, ReactNode } from 'react';
import { styled } from '@mui/material';
import { shouldForwardProp as sysShouldForwardProp } from '@mui/system';
import colors from '../theme/colors';
import Box, { BoxProps } from '../Box';

interface StickyBarOwnerState {
  /**
   * Disable default padding inside StickyBar
   */
  noSpacing?: boolean;

  /**
   * Set sticky position on screen
   */
  position: 'top' | 'bottom';

  /**
   * Set StickyBar background color
   */
  color?: 'white' | 'internal' | 'transparent';

  /**
   * Set elevation value to add box-shadow
   */
  elevation?: number;

  /**
   * StickyBar content, here you can use Container or other children components
   */
  children?: ReactNode;
}

type BoxPropsWithoutConflicts = Omit<BoxProps, 'position' | 'color'>;

export interface StickyBarProps extends BoxPropsWithoutConflicts, StickyBarOwnerState {}

const getBackgroundColor = (color?: string) => {
  switch (color) {
    case 'white':
      return colors.basic.white;
    case 'internal':
      return colors.internal[200];
    case 'transparent':
      return 'none';
    default:
      return color;
  }
};

const StyledStickyBar = styled(Box, {
  name: 'StickyBar',
  slot: 'Root',
  shouldForwardProp: (prop) =>
    sysShouldForwardProp(prop) && prop !== 'noSpacing' && prop !== 'position' && prop !== 'color',
})<BoxPropsWithoutConflicts & { ownerState: StickyBarOwnerState }>(
  ({ theme, ownerState: { noSpacing, position, color, elevation } }) => ({
    display: 'block',
    position: 'sticky',
    zIndex: theme.zIndex.appBar,
    backgroundColor: getBackgroundColor(color),
    top: position === 'top' ? 0 : undefined,
    bottom: position === 'bottom' ? 0 : undefined,
    padding: noSpacing ? 0 : theme.spacing(2, 0),
    [theme.breakpoints.up('sm')]: {
      padding: noSpacing ? 0 : theme.spacing(2, 4),
    },
    ...(elevation && {
      boxShadow: theme.shadows[elevation],
    }),
  })
);

const StickyBar: FC<StickyBarProps> = ({ children, color = 'white', noSpacing, position, elevation, ...other }) => {
  return (
    <StyledStickyBar
      color={color}
      ownerState={{
        color,
        position,
        noSpacing,
        elevation,
      }}
      {...other}
    >
      {children}
    </StyledStickyBar>
  );
};

export default StickyBar;
